"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { BookmarkPlus, MailCheck, CheckCircle2, UserPlus, LogIn } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { trackAccountEvent } from "@/lib/analytics";
import {
  dismissSavePrompt,
  isSavePromptDismissed,
  savePromptState,
} from "@/lib/savePrompt";

interface SaveChatPromptProps {
  chatId?: string;
  sessionId?: string;
  className?: string;
}

/**
 * The card below a sent study email that offers an account. It is UI only and
 * never part of the chat's messages.
 */
export function SaveChatPrompt({ chatId, sessionId, className }: SaveChatPromptProps) {
  const { accountsEnabled, isLoading, isAuthenticated, pendingVerificationEmail, openAuthModal } =
    useAuth();
  const [dismissed, setDismissed] = useState(() => isSavePromptDismissed(chatId));
  const shownForChatRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    setDismissed(isSavePromptDismissed(chatId));
  }, [chatId]);

  const state = isLoading
    ? "hidden"
    : savePromptState({
        emailSent: true,
        accountsEnabled,
        isAuthenticated,
        dismissed,
        pendingVerification: pendingVerificationEmail !== null,
      });

  useEffect(() => {
    if (state !== "prompt") return;
    const key = chatId ?? "";
    if (shownForChatRef.current === key) return;
    shownForChatRef.current = key;
    trackAccountEvent("save_prompt_shown", { sessionId, chatId });
  }, [state, chatId, sessionId]);

  if (state === "hidden") return null;

  const frame = cn(
    "rounded-xl bg-(--outer-bg) border border-border shadow-xs",
    className
  );
  const entry = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    // Enters after the green "E-Mail erfolgreich gesendet!" card.
    transition: { duration: 0.3, ease: "easeOut" as const, delay: 0.2 },
  };

  if (state === "saved" || state === "pending-verification") {
    const saved = state === "saved";
    return (
      <motion.div {...entry} className={frame}>
        <div className="flex items-center gap-3 p-4">
          <div className="shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            {saved ? (
              <CheckCircle2 className="h-5 w-5 text-primary-600" />
            ) : (
              <MailCheck className="h-5 w-5 text-primary-600" />
            )}
          </div>
          <p className="text-sm text-foreground">
            {saved
              ? "Dieser Chat ist in Ihrem Konto gespeichert."
              : "Bitte bestätigen Sie Ihre E-Mail, dann ist dieser Chat in Ihrem Konto gespeichert."}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div {...entry} className={cn(frame, "hover:shadow-md transition-shadow")}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <BookmarkPlus className="h-5 w-5 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">Chat speichern?</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Mit einem kostenlosen Konto finden Sie diesen Chat und Ihre Studienanfrage
              jederzeit wieder, auch auf anderen Geräten. So können Sie später gezielt
              Fragen zu Ihrer Erkrankung und zur Studie stellen.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 pb-4">
        <motion.button
          type="button"
          onClick={() => {
            trackAccountEvent("save_prompt_signup_click", { sessionId, chatId });
            openAuthModal("signup", { chatId });
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg shadow-xs transition-all bg-linear-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white hover:shadow-md"
        >
          <UserPlus className="h-4 w-4" />
          <span>Konto erstellen</span>
        </motion.button>

        <motion.button
          type="button"
          onClick={() => {
            trackAccountEvent("save_prompt_login_click", { sessionId, chatId });
            openAuthModal("login", { chatId });
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-background hover:bg-muted text-foreground rounded-lg border border-border shadow-xs hover:shadow-md transition-all"
        >
          <LogIn className="h-4 w-4" />
          <span>Anmelden</span>
        </motion.button>

        <button
          type="button"
          onClick={() => {
            dismissSavePrompt(chatId);
            setDismissed(true);
          }}
          className="px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg transition-colors"
        >
          Später
        </button>
      </div>
    </motion.div>
  );
}
